import Enum from 'enum';
import moment from 'moment';
import { fromJS, List, Map } from 'immutable';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import isElectron from 'is-electron';
import ConferencesResource from 'flow/api/conferences';
import { ContactType } from 'flow/utils/extension-utils';

import appConfig from 'app-config';
import { isEmptyNullOrUndefined } from 'flow/utils';
import { getNumberLookup } from 'flow/selectors/selectors';
import { isMacOs, isWindowsOs } from 'flow/utils/os-utils-flow';
import { isTeamsFullClient } from 'flow/utils/app-utils';
import { i18next } from 'flow/configure-locale';
import { getBrand } from 'flow/brands/brands';

export const CallState = new Enum(
    [
        'DOWN',
        'RESERVED',
        'OFFHOOK',
        'DIALING',
        'RING',
        'RINGING',
        'UP',
        'BUSY',
        'PARKED',
        'NOT_FOUND',
        'UNAVAILABLE',
        'UNKNOWN',
    ],
    { freez: true, ignoreCase: true },
);

export const RuleTypes = new Enum(['blocked_in', 'blocked_out', 'priority'], {
    ignoreCase: true,
});

export const CallTransferType = new Enum(['blind', 'attended'], {
    freez: true,
    ignoreCase: true,
});

export const AttendedTransferState = new Enum(
    ['pending', 'complete', 'cancel', 'unknown'],
    { freez: true, ignoreCase: true },
);

export const CallMethodType = new Enum(
    {
        ALWAYS_ASK: 'alwaysAsk',
        LOCAL: 'local',
        IP_PHONE: 'ipPhone',
        MOBILE: 'mobile',
    },
    { freez: true },
);

export const IncomingCallSetting = new Enum(
    {
        RING: 'ring',
        SILENT: 'silent',
        DO_NOT_RING: 'doNotRing',
    },
    { freez: true },
);

// The order of SILENT and RING is important since it affects the order
// they should be rendered on the Ring tone and volume settings page.
export const PlayRingtoneSetting = new Enum(
    {
        SILENT: 'silent',
        RING: 'ring',
    },
    { freez: true },
);

export const ExtraHeaders = new Enum(
    {
        CONFERENCE_PIN: 'X-TVX-CONFERENCE-PIN',
        CONFERENCE_MEETING_CODE: 'X-TVX-CONFERENCE-MEETING-CODE',
    },
    { freez: true },
);

export const ActiveCallDirections = new Enum(
    {
        INCOMING: 'local_start',
        OUTGOING: 'remote_start',
    },
    { freez: true },
);

export const NewCallBlockReason = new Enum(
    [
        'NONE',
        'TOO_MANY_CALLS',
        'CALL_WAITING_OFF_TOO_MANY_CALLS',
        'TOO_MANY_CALLS',
        'CALLS_NOT_ESTABLISHED',
        'TRANSFER_IN_PROGRESS',
        'CO_LISTENING_IN_PROGRESS',
        'GEO_LOCATION_BLOCKED',
        'COMPANION_REQUIRED',
    ],
    { freez: true, ignoreCase: true },
);

const ORIGINATION_ONLY_BLOCK_REASONS = [
    NewCallBlockReason.GEO_LOCATION_BLOCKED,
    NewCallBlockReason.COMPANION_REQUIRED,
];

export const blocksOnlyOrigination = (reason) =>
    ORIGINATION_ONLY_BLOCK_REASONS.includes(reason);

// The default "relative country" of the number formatting. If no other country
// code is supplied, numbers are formatted as being an international or national
// number relative to this country code.
// It is also the default country code guess when parsing phone numbers.
const DEFAULT_COUNTRY_CODE = 'SE';

export const CallButtonLocation = new Enum(
    ['SEARCH_BAR', 'DIALPAD', 'CENTRAL_SWITCH_WIDGET'],
    { freez: true, ignoreCase: true },
);
/**
 * Note: this method is a bit naive and can't handle whenever the duration exceeds 23:59:59, and the duration
 * turns into days.
 * @param duration          A moment.js Duration
 * @param defaultString     What to return if all else fails
 * @returns {String} A human-readable duration String
 */

export const BLIND_TRANSFER_SETTINGS = new Enum([
    'ACTIVE',
    'RETURN_DESTINATION',
    'RETURN_DELAY',
]);

export const getPrettyDuration = (duration, defaultString = '') => {
    if (!duration) {
        return defaultString;
    }

    if (duration.days() > 0) {
        const days = `${duration.days()}${i18next.t('Time.Day.Short')}`;
        const hours = `${duration.hours()}${i18next.t('settings.profile.short_hour')}`;
        return `${days} ${hours}`;
    }

    if (duration.hours() > 0) {
        // The .utc() is so that the .format() doesn't display the duration in local time.
        // See https://momentjs.com/docs/#/parsing/utc/
        return moment(duration.asMilliseconds()).utc().format('HH:mm:ss');
    } else if (duration >= 0) {
        return moment(duration.asMilliseconds()).format('mm:ss');
    } else {
        return defaultString;
    }
};

export const numberIsValidMatcher = (number) => {
    return (
        !isEmptyNullOrUndefined(number) &&
        (number.startsWith('0') || number.startsWith('+')) &&
        /^\+?\d*\*?$/.test(number)
    );
};

export const numberIsNationalOrInternational = (number) => {
    return !isEmptyNullOrUndefined(number) && /^\+?[\d|*|#|٠-٩]*$/.test(number);
};

/**
 * Naive control to validate if an input is a valid number
 *
 * @param number
 * @returns {boolean}
 */
export const isValidSaveableNumber = (number) => {
    return (
        isEmptyNullOrUndefined(number) ||
        numberIsNationalOrInternational(number)
    );
};

/**
 * Return whether or not a user has a registered sip terminal.
 *
 * @param extension
 * @returns {*|boolean}
 */
export const isRegisteredSIP = (extension) => {
    return (
        extension &&
        extension.has('sipInfo') &&
        extension.getIn(['sipInfo', 'registrationExpiry']) > 0
    );
};
export const getContactFromNumber = (
    contacts = List(),
    numberLookups = Map(),
    number,
) => {
    return getContactFromSourcesByNumber(
        contacts,
        numberLookups,
        Map(),
        number,
    );
};

export const getContactFromSourcesByNumber = (
    contacts,
    numberLookups,
    selectedCtiContact,
    number,
) => {
    if (selectedCtiContact && selectedCtiContact.get('contactRoute')) {
        return selectedCtiContact;
    }
    const contact = contacts.find((contact) => {
        if (!contact) {
            return false;
        } else {
            return (
                contact.getIn(['fixed', 'nationalFormat'], '') === number ||
                contact.getIn(['mobile', 'nationalFormat'], '') === number ||
                contact.getIn(['fixed', 'number'], '') === number ||
                contact.getIn(['technicalNumber', 'number'], '') === number ||
                contact.getIn(['mobile', 'number'], '') === number ||
                contact.getIn(['phoneNumber', 'number'], '') === number
            );
        }
    });
    if (contact) {
        return contact;
    }
    return getNumberLookup(numberLookups, number);
};

const hasIncomingDirection = (activeCall) => {
    return (
        activeCall &&
        activeCall.has('direction') &&
        activeCall.get('direction') === ActiveCallDirections.INCOMING.value &&
        activeCall.has('source')
    );
};

const hasOutgoingDirection = (activeCall) => {
    return (
        activeCall &&
        activeCall.has('direction') &&
        activeCall.get('direction') === ActiveCallDirections.OUTGOING.value &&
        activeCall.has('destination')
    );
};

export const shouldIncomingCallNotificationBeDispatched = (
    oldActiveCall,
    newActiveCall,
) => {
    if (!newActiveCall) {
        return false;
    }

    const callKeyHasChanged =
        !oldActiveCall || oldActiveCall.get('key') !== newActiveCall.get('key');
    const newCallHasIncomingDirection = hasIncomingDirection(newActiveCall);
    const oldCallHasIncomingDirection = hasIncomingDirection(oldActiveCall);

    if (callKeyHasChanged) {
        return newCallHasIncomingDirection;
    } else {
        return newCallHasIncomingDirection && !oldCallHasIncomingDirection;
    }
};

export const removeExtraCharacters = (number) => {
    if (typeof number === 'string') {
        return number.replace(/-|\(|\)|\s/g, '');
    }
    return number;
};

export const getExtensionKeyByContact = (
    contact,
    contactKeyToExtensionKeyMap,
) => {
    if (!contact || !ContactType.bound.is(contact.get('type'))) {
        return null;
    }

    const contactKey = contact.get('key');
    return contactKeyToExtensionKeyMap.get(contactKey);
};

export const contactHasNumber = (number, contact) => {
    if (!number || !contact) {
        return false;
    }
    const numberString = Map.isMap(number)
        ? number.get('nationalFormat')
        : number;
    const contactNumbers = List()
        .push(contact.getIn(['fixed', 'number']))
        .push(contact.getIn(['fixed', 'nationalformat']))
        .push(contact.getIn(['mobile', 'number']))
        .push(contact.getIn(['mobile', 'nationalformat']));

    const strippedNumber = removeExtraCharacters(numberString);
    const strippedContactNumbers = contactNumbers.map(removeExtraCharacters);

    return strippedContactNumbers.includes(strippedNumber);
};

export const getCountryCodeFromNumber = (number) => {
    if (!number) {
        return undefined;
    }
    const numberObject = parsePhoneNumberFromString(number);
    return numberObject ? numberObject.country : undefined;
};

/**
 * Take a number on an E164-format, and format it relative to the given country
 * code. An E164-formatted number is a number like '+CC XXX ... XX', where 'CC'
 * is the calling code for a country.
 *
 * If the number is in another country than the relative country code given,
 * it's returned with an international prefix ('+') and its country code. If
 * it's in the same country, it's returned in a local format without prefix and
 * calling code.
 *
 * If the number supplied is not a valid E164-number, its returned unmodified.
 *
 * Example 1: we pass a Danish internationally formatted number in,
 * '+4533608842', and we want to format it for a relative countryCode 'SE'. The
 * number contains enough information to see that it is a Danish number, and
 * the function returns a pretty-printed '+45 33 60 88 42'.
 *
 * Example 2: we pass a Danish internationally formatted number in,
 * '+4533608842', and we want to format it for country code 'DK'. Since this
 * number is identifiably Danish, the function strips the IID-prefix and
 * country calling code, and returns a pretty-printed '33 60 88 42'.
 *
 * @param {String} numberToFormat
 * @param {String} relativeCountryCode
 */
export const prettyFormatE164Number = (numberToFormat, relativeCountryCode) => {
    if (!relativeCountryCode) {
        relativeCountryCode = DEFAULT_COUNTRY_CODE;
    }
    if (!numberToFormat || !numberToFormat.startsWith('+')) {
        return numberToFormat;
    }
    const parsed = parsePhoneNumberFromString(numberToFormat);

    if (!parsed || Object.keys(parsed).length === 0) {
        return numberToFormat;
    }
    if (parsed.country === relativeCountryCode.toUpperCase()) {
        return prettyFormatE164NumberNational(parsed);
    } else {
        return prettyFormatE164NumberInternational(parsed);
    }
};

const prettyFormatE164NumberInternational = (numberToFormat) => {
    return numberToFormat.formatInternational();
};

const prettyFormatE164NumberNational = (numberToFormat) => {
    return numberToFormat.formatNational();
};

/**
 * Format an E164 number to to top friendly number
 *
 * Top wants numbers in national format for swedish number but international with leading double zeros for non-swedish numbers
 *
 * @param {String} numberToFormat
 */
export const formatE164NumberToTopFriendly = (numberToFormat) => {
    return numberToFormat.replace('+46', '0').replace('+', '00');
};

/**
 * @param {String} number       The number we want to format as an E164-number
 * @param {String} countryCode  A default country code used as fallback when
 *                              parsing.
 */
export const convertNumberToE164 = (
    number,
    countryCode = DEFAULT_COUNTRY_CODE,
) => {
    if (typeof number !== 'string') {
        return number;
    }

    const parsed = parsePhoneNumberFromString(
        number,
        countryCode.toUpperCase(),
    );
    if (parsed && parsed.isValid()) {
        return parsed.number;
    }
    return number;
};

export const getRoutingDescription = (blocklist, i18next) => {
    if (blocklist.get('matcher')) {
        return blocklist.get('matcher');
    } else if (blocklist.has('blocklistPredefDTO')) {
        return blocklist.getIn(
            ['blocklistPredefDTO', 'description'],
            i18next.t('status.unknown'),
        );
    }
    return i18next.t('status.unknown');
};

//This is old and shouldn't be used in any new places
export const inCall = (activeCall) => {
    return activeCall && activeCall.get('state') !== 'down';
};

export const getNumberFromTelCalltoUrl = (url) => {
    if (!url) return null;

    /**
     * Main reason for decoding is to convert instances of %20 to whitespaces
     */
    const decodedString = decodeURIComponent(url);

    const telCalltoPrefix =
        decodedString.match(/^tel:\/\//) ||
        decodedString.match(/^callto:\/\//) ||
        decodedString.match(/^tel:/) ||
        decodedString.match(/^callto:/);
    const number = decodedString.replace(telCalltoPrefix, '');

    /**
     * Stripping whitespaces and dashes. Although there -should- be no such characters in a tel and callto number,
     * we've learned that hitta.se does indeed have a space between tel: and the number. Ex: tel: 123456
     */
    const whiteSpacesAndDashesAndSlashes = /\s|-|\//g;
    return number.replace(whiteSpacesAndDashesAndSlashes, '');
};

export const getProtocolType = (url) => {
    if (!url || !url.includes(':')) {
        return 'unknown';
    }

    return url.split(':')[0];
};

export const getUserAgentName = (embedded) => {
    const clientType = getClientTypeForUserAgent(embedded);
    const brandNameNoSpace = (
        getBrand(true).brandNameUIFriendly || 'flow'
    ).replace(/[^A-Za-z0-9]+/g, '');
    const environment =
        appConfig.environment === 'production'
            ? ''
            : '-' + appConfig.environment;
    const isNoUpdate = process.env.NO_UPDATE === 'true' ? '-NU' : '';
    const version =
        '-' +
        (isElectron()
            ? `${require('@electron/remote').app.getVersion()}${isNoUpdate}`
            : appConfig.version);
    return brandNameNoSpace + clientType + environment + version;
};

const getClientTypeForUserAgent = (embedded) => {
    if (isElectron()) {
        return '-tvx-desktop';
    } else if (isTeamsFullClient(embedded)) {
        // TEAMSFVMANUAL shares this suffix until it gets a client type of its own.
        return '-teamsfv';
    } else {
        return '-tvx-web';
    }
};

export const callShouldBeAnsweredAutomatically = (rtcSessionData) => {
    try {
        /**
         * Alert-Info: AA = Auto answer
         *
         * When receiving the header from...
         *    ClearIT, it's "AA" (with fnuttz)
         *    Telavox old Desktop application, it's AA (without fnuttz)
         */

        const alertInfoHeader = rtcSessionData.request.getHeader('Alert-Info');
        return (
            rtcSessionData.originator === 'remote' &&
            (alertInfoHeader === 'AA' || alertInfoHeader === '"AA"')
        );
    } catch (_e) {
        return false;
    }
};

export const removeNotAllowedDTMFCharacter = (input) => {
    return input.replace(/[^0-9#*]/g, '');
};

export const removeNotAllowedPhoneNumberCharacters = (input) => {
    return input.replace(/[^\d#*+\u0660-\u0669]/g, '');
};

export const isNewCallStarted = (prevCallState, newCallState) =>
    !!prevCallState &&
    !!newCallState &&
    CallState.RINGING.is(prevCallState) &&
    CallState.UP.is(newCallState);

export const senderSupportsDtmf = (sender) => {
    if (!sender) {
        return false;
    }

    return !!sender.canInsertDTMF;
};

export const sessionHasAudioTracks = (session) => {
    const audioSenders =
        session.connection &&
        session.connection.getSenders().filter((sender) => {
            return sender.track && sender.track.kind === 'audio';
        });
    return !!audioSenders && audioSenders.length > 0;
};

export const getRedirectNumber = (activeCall) => {
    if (!activeCall) {
        return null;
    }

    const redirectNumber = activeCall.get('rdnis', null);
    const originalRedirectNumber = activeCall.get('originalRdnis', null);

    return originalRedirectNumber || redirectNumber;
};

/**
 * Get the number of the person who the telavox user is talking to, no matter who called who.
 */
export const getNumberOfOtherPerson = (activeCall) => {
    if (hasOutgoingDirection(activeCall)) {
        return activeCall.get('destination');
    } else if (hasIncomingDirection(activeCall)) {
        return activeCall.get('source');
    }
    return null;
};

/**
 * parse number to national format, contains only digits
 */
export const toNationalNumberFormat = (numberToFormat) => {
    if (!numberToFormat || !numberToFormat.startsWith('+')) {
        return numberToFormat;
    }
    const phoneNumber = parsePhoneNumberFromString(numberToFormat);
    return phoneNumber.formatNational().replace(/\D/g, '');
};

export const stripAndConvertNumberToE164 = (number, countryCode) => {
    const strippedNumber = removeExtraCharacters(number);
    return convertNumberToE164(strippedNumber, countryCode);
};

export const convertE164NumberToTelavoxRoutingNumber = (number) => {
    if (typeof number !== 'string') {
        return undefined;
    }
    return number.startsWith('+') ? '00' + number.substring(1) : number;
};

export const isQueueCall = (session) => !!session.data['isQueueCall'];

// This function filters out a=crypto line in a SDP. This is added in the SDP if a e.g. terminal uses SRTP (the offer being RTP+SDES)
// Chromium does not as of now support RTP+DTLS and RTP+SDES offering when offered together.
export const filterOutCryptoLineInSDP = (sdp) => {
    return sdp
        .split('\n')
        .filter((sdpLine) => !sdpLine.startsWith('a=crypto:'))
        .join('\n');
};

/**
 *
 * @param {string} header - Input header string
 * @returns {string|null} - The extracted digits or null if no match is found
 */
export const filterOutPaiHeader = (header) => {
    if (!header) {
        return null;
    }
    const regex = /(\+?\d+)/;
    const match = header.match(regex);
    return match ? match[1] : null;
};

//This is done to slim down the sdp message since we've had problems with too large messages with some customers.
//We remove all tcp ice candidates since we only use udp.
export const filterOutTCPCandidates = (sdp) => {
    return sdp
        .split('\n')
        .filter(
            (line) =>
                !(
                    line.startsWith('a=candidate:') &&
                    line.toLowerCase().includes(' tcp ')
                ),
        )
        .join('\n');
};

//Todo this is a function copied from the new client. We should use the new one when its live
export const getMeetingPinFromAPI = (meetingCode) => {
    return new Promise((resolve, reject) => {
        const resultCallback = (result) => {
            const conferencePin = fromJS({
                pin: result.pin,
            });
            resolve(conferencePin);
        };

        ConferencesResource.getMeetingCodeConference(
            {
                onResult: (result) => resultCallback(result),
                onError: (error) => reject(error),
                onClientError: (error) => reject(error),
            },
            meetingCode,
            'contact,live',
        );
    });
};

export const textFieldInputIsCallable = (filterText) => {
    const strippedNumber = removeExtraCharacters(filterText);

    const firstChar = strippedNumber ? strippedNumber.charAt(0) : '';
    return (
        firstChar === '+' || firstChar === '*' || !isNaN(parseInt(firstChar))
    );
};

export const getHoverTextShortcutMic = () => {
    if (isMacOs()) {
        return ' (\u2318 + D)';
    }
    if (isWindowsOs()) {
        return ' (Ctrl + D)';
    }
    return '';
};
